import { apiDelete, apiGet, apiPost, apiPut, apiPutPath } from '../support/helpers/ApiConnection.Helper';
import ReferralContactEntity from '../entities/ReferralContact.Entity';
import ReferralsClaimsInfoEntity from '../entities/ReferralsClaimsInfo.Entity';
import ReferralEntity from '../entities/Referral.Entity';
import { ReferralStatusTypesEnum } from '../support/enums/ReferralStatusTypesEnum';
import ReferralClaimEntity from '../entities/ReferralClaim.Entity';
import ImeDataModel from '../data-models/Ime.DataModel';
import ReferralDocumentDataModel from '../data-models/ReferralDocument.DataModel';
import ReferralDocumentEntity from '../entities/ReferralDocument.Entity';
import { ReferralOverviewDataModel } from '../data-models/ReferralOverview.DataModel';
import ReferralScheduleEntity from '../entities/ReferralSchedule.Entity';
import ReferralResponseDataModel from '../data-models/ReferralResponse.DataModel';
import { ServicesEnum } from '../support/enums/Services.Enum';
import { ReferralOverviewImeDataModel } from '../data-models/ReferralOverviewIme.DataModel';
import { ReferralOverviewTcmDataModel } from '../data-models/ReferralOverviewTcm.DataModel';
import ImeScorecardResponseEntity from '../entities/ImeScorecardResponse.Entity';
import SubmissionEntity from '../entities/Submission.Entity';
import ReferralSubmitDataModel from '../data-models/ReferralSubmit.DataModel';
import IMECoverLetterEntity from '../entities/IMECoverLetter.Entity';
import TcmCoverLetterEntity from '../entities/tcm/TcmCoverLetter.Entity';
import TcmTelephonicCaseManagementEntity from '../entities/tcm/TcmTelephonicCaseManagement.Entity';
import TcmMedicalInfoEntity from '../entities/tcm/TcmMedicalInfo.Entity';
import TcmTreatingProviderEntity from '../entities/tcm/TcmTreatingProvider.Entity';
import ServiceTypeRequestFcmEntity from '../entities/fcm/ServiceTypeRequestFcm.Entity';
import ServiceTypeRequestRtwEntity from '../entities/rtw/ServiceTypeRequestRtw.Entity';
import TcmOfficeVisitEntity from '../entities/tcm/TcmOfficeVisit.Entity';
import TcmWorkStatusEntity from '../entities/tcm/TcmWorkStatus.Entity';
import ReferralNoteTcmEntity from '../entities/ReferralNoteTcm.Entity';
import ReferralNoteFcmEntity from '../entities/ReferralNoteFcm.Entity';
import { ReferralOverviewFcmDataModel } from '../data-models/ReferralOverviewFcm.DataModel';
import MedicalServiceDataModel from '../data-models/MedicalService.DataModel';
import DiagnosticServiceDataModel from '../data-models/DiagnosticService.DataModel';
import DiagnosticProcedureResponseScorecardTabDataModel from '../data-models/DiagnosticProcedureResponseScorecardTabDataModel';
import ReferralErrorListDataModel from '../data-models/ReferralErrorList.DataModel';
import UtilizationReviewServiceDataModel from '../data-models/UtilizationReviewService.DataModel';
import ReferralStatus from '../data-models/AdminReferralStatusEntity';
import TransportationDataModel from '../data-models/Transportation.DataModel';
import TranslationDataModel from '../data-models/Translation.DataModel';
import AdminReferralTableDataModel from '../data-models/AdminReferralTableDataModel';
import { ReferralSearchFilter } from '../components/tc-admin/referrals/Referrals.Component';
import AdminReferralSentCountDataModel from '../data-models/AdminReferralSentCountDataModel';
import AdminReferralSentCountByDateDataModel from '../data-models/AdminReferralSentCountByDate.DataModel';
import AdminReferralByDateAndServiceDataModel from '../data-models/AdminReferralByDateAndService.DataModel';
import AdminReferralVendorDocumentsTableDataModel from '../data-models/AdminReferralVendorDocumentsTable.DataModel';
import ReferralVendorDocumentDataModel from '../data-models/ReferralVendorDocument.DataModel';
import { TtDataModel } from '../data-models/Tt.DataModel';
import DmeServiceDataModel from '../data-models/dme/DmeService.DataModel';
import DmeResponseDataModel from '../data-models/dme/DmeResponse.DataModel';
import SiuVendorNote from '../entities/SiuVendorNote.Entity';
import RtwVendorNote from '../entities/RtwVendorNote.Entity';
import SiuVendorSummary from '../entities/SiuVendorSummary.Entity';
import SurveillanceServiceDataModel from '../data-models/SurveillanceService.DataModel';

export default function getReferrals(
    abortSignal: AbortSignal,
    claimId?: number,
    dateFrom?: string,
    dateTo?: string,
): Promise<Array<ReferralEntity>> {
    const url = '/referrals';

    let queryString = '?';

    if (claimId) {
        queryString += 'claimId=' + claimId;
    }

    if (dateFrom !== null && dateFrom !== undefined) {
        if (claimId) {
            queryString += '&';
        }
        queryString += `dateFrom=${dateFrom}`;
    }

    if (dateTo !== null && dateTo !== undefined) {
        if ((dateFrom !== null && dateFrom !== undefined) || claimId) {
            queryString += '&';
        }
        queryString += `dateTo=${dateTo}`;
    }

    return apiGet<Array<ReferralEntity>>(url + queryString, abortSignal);
}

export function getReferral(referralId: number, abortSignal: AbortSignal): Promise<ReferralEntity> {
    return apiGet<ReferralEntity>('/referrals/' + referralId, abortSignal);
}

const ACTIVE_TCM_REFERRAL_STATUSES: number[] = [
    ReferralStatusTypesEnum.Open,
    ReferralStatusTypesEnum.ValidationFailed,
    ReferralStatusTypesEnum.InProgress,
    ReferralStatusTypesEnum.ReadyForVendor,
    ReferralStatusTypesEnum.PendingSubmission,
    ReferralStatusTypesEnum.SubmissionInProgress,
    ReferralStatusTypesEnum.ReadyForSubmission,
    ReferralStatusTypesEnum.SubmissionFailed,
];

export async function getActiveReferralForVendorAndClaim(
    claimId: number,
    vendorId: number,
    abortSignal: AbortSignal,
): Promise<ReferralEntity | null> {
    const referrals = await getReferrals(abortSignal, claimId);
     return referrals.find(
         (r) => r.vendorId === vendorId && ACTIVE_TCM_REFERRAL_STATUSES.includes(r.referralStatusId),
     ) ?? null;
}

export interface SendReferralInitialDataParams {
    claimId: number;
    serviceTypeIds: Array<number>;
    vendorId: number;
    abortSignal: AbortSignal;
}

export function sendReferralInitialData(params: SendReferralInitialDataParams): Promise<ReferralEntity> {
    const referral: ReferralEntity = {
        claimId: params.claimId,
        serviceTypeIds: params.serviceTypeIds,
        vendorId: params.vendorId,
    };
    return apiPost<ReferralEntity>('/referrals', referral, params.abortSignal);
}

export interface SendSiuReferralParams {
    claimId: number;
    serviceTypeIds: Array<number>;
    vendorId: number;
    facilityTypeIds: number[];
    eRemoteUnmannedNumberOfDays: number | null;
    surveillanceMannedNumberOfDays: number | null;
    backgroundInvestigationOption: string | null;
    eRemoteUnmannedOption: string | null;
    medicalCanvassNumberOfFacilities: number | null;
    abortSignal: AbortSignal;
}

export function sendSiuReferralInitialData(params: SendSiuReferralParams): Promise<ReferralEntity> {
    return apiPost<ReferralEntity>('/referrals/siu', {
        claimId: params.claimId,
        serviceTypeIds: params.serviceTypeIds,
        vendorId: params.vendorId,
        facilityTypeIds: params.facilityTypeIds,
        eRemoteUnmannedNumberOfDays: params.eRemoteUnmannedNumberOfDays ?? undefined,
        surveillanceMannedNumberOfDays: params.surveillanceMannedNumberOfDays ?? undefined,
        backgroundInvestigationOption: params.backgroundInvestigationOption ?? undefined,
        eRemoteUnmannedOption: params.eRemoteUnmannedOption ?? undefined,
        medicalCanvassNumberOfFacilities: params.medicalCanvassNumberOfFacilities ?? undefined,
    }, params.abortSignal);
}

export interface SendFcmReferralParams {
    claimId: number;
    serviceTypeIds: Array<number>;
    vendorId: number;
    rushRequest?: boolean;
    additionalInformation?: string | null;
    abortSignal: AbortSignal;
}

export function sendFcmReferralInitialData(params: SendFcmReferralParams): Promise<ReferralEntity> {
    return apiPost<ReferralEntity>('/referrals/fcm', {
        claimId: params.claimId,
        serviceTypeIds: params.serviceTypeIds,
        vendorId: params.vendorId,
        rushRequest: params.rushRequest ?? undefined,
        additionalInformation: params.additionalInformation ?? undefined,
    }, params.abortSignal);
}

export interface SendRtwReferralParams {
    claimId: number;
    serviceTypeIds: Array<number>;
    vendorId: number;
    rushRequest?: boolean;
    physicalRestrictions: string;
    primaryLanguage: string;
    hourlyRate: string;
    isUnionMember: string;
    requiresAlternateHoursEnabled?: boolean;
    transitionHoursPerWeek?: string | null;
    requiresAlternateScheduleEnabled?: boolean;
    workSchedulePreferences?: string | null;
    lightDutyLocation: string;
    additionalInformation?: string | null;
    abortSignal: AbortSignal;
}

export function sendRtwReferralInitialData(params: SendRtwReferralParams): Promise<ReferralEntity> {
    return apiPost<ReferralEntity>('/referrals/rtw', {
        claimId: params.claimId,
        serviceTypeIds: params.serviceTypeIds,
        vendorId: params.vendorId,
        rushRequest: params.rushRequest ?? undefined,
        physicalRestrictions: params.physicalRestrictions,
        primaryLanguage: params.primaryLanguage,
        hourlyRate: params.hourlyRate,
        isUnionMember: params.isUnionMember,
        requiresAlternateHoursEnabled: params.requiresAlternateHoursEnabled ?? undefined,
        transitionHoursPerWeek: params.transitionHoursPerWeek ?? undefined,
        requiresAlternateScheduleEnabled: params.requiresAlternateScheduleEnabled ?? undefined,
        workSchedulePreferences: params.workSchedulePreferences ?? undefined,
        lightDutyLocation: params.lightDutyLocation,
        additionalInformation: params.additionalInformation ?? undefined,
    }, params.abortSignal);
}

export function getReferralClaim(referralId: number, abortSignal: AbortSignal): Promise<ReferralClaimEntity> {
    return apiGet<ReferralClaimEntity>('/referrals/' + referralId + '/claims', abortSignal);
}

export function getReferralContact(
    referralId: number,
    referralContactTypeId: number,
    abortSignal: AbortSignal,
): Promise<ReferralContactEntity> {
    return apiGet<ReferralContactEntity>(
        '/referrals/' + referralId + '/referral-contacts',
        abortSignal,
        'referralContactTypeId=' + referralContactTypeId,
    );
}

export function GetRefreshedReferralContacts(
    referralId: number,
    referralContactTypeId: number,
    abortSignal: AbortSignal,
): Promise<ReferralsClaimsInfoEntity> {
    return apiGet<ReferralsClaimsInfoEntity>(
        '/referrals/' + referralId + '/claims-info',
        abortSignal,
        'referralContactTypeId=' + referralContactTypeId,
    );
}


export function getReferralSchedule(referralId: number, abortSignal: AbortSignal): Promise<ReferralScheduleEntity> {
    return apiGet<ReferralScheduleEntity>('/referrals/' + referralId + '/schedules', abortSignal);
}

export function getReferralDocument(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<Array<ReferralDocumentEntity>> {
    return apiGet<Array<ReferralDocumentEntity>>('/referrals/' + referralId + '/documents', abortSignal);
}

export function getReferralIme(referralId: number, abortSignal: AbortSignal): Promise<ImeDataModel> {
    return apiGet<ImeDataModel>('/referrals/' + referralId + '/imes', abortSignal);
}

export function sendReferralDocuments(
    referralId: number,
    documentsBody: ReferralDocumentDataModel,
    abortSignal: AbortSignal,
): Promise<Array<ReferralDocumentEntity>> {
    return apiPost('/referrals/' + referralId + '/documents', documentsBody, abortSignal);
}

export function sendReferralSubmit(
    referralId: number,
    abortSignal: AbortSignal,
    isTt = false,
): Promise<ReferralSubmitDataModel> {
    let url = '/referrals/' + referralId + '/submit';
    if (isTt) {
        url += '/tt';
    }

    return apiPost(url, null, abortSignal, false);
}

export function getPermission(
    referralId: number,
    claimId: number,
    abortSignal: AbortSignal,
): Promise<ReferralOverviewDataModel> {
    return apiGet('/referrals/' + referralId + '/permission/' + claimId, abortSignal);
}

export function getOverview(referralId: number, abortSignal: AbortSignal): Promise<ReferralOverviewDataModel> {
    return apiGet('/referrals/' + referralId + '/info', abortSignal);
}

export function getOverviewIme(referralId: number, abortSignal: AbortSignal): Promise<ReferralOverviewImeDataModel> {
    return apiGet('/referrals/' + referralId + '/overview-ime', abortSignal);
}

export function getOverviewTcm(referralId: number, abortSignal: AbortSignal): Promise<ReferralOverviewTcmDataModel> {
    return apiGet('/referrals/' + referralId + '/overview-tcm', abortSignal);
}

export function getReferralResponse(
    referralId: number,
    abortSignal: AbortSignal,
    service: ServicesEnum | number,
): Promise<ReferralResponseDataModel> {
    return apiGet('/referrals/' + referralId + '/response', abortSignal, 'service=' + service);
}

export function getServiceTypeResponse<T>(
    referralId: number,
    abortSignal: AbortSignal,
    service: ServicesEnum | number,
): Promise<T> {
    return apiGet<T>('/referrals/' + referralId + '/response', abortSignal, 'service=' + service);
}

export function getImeScorecardResponse(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<Array<ImeScorecardResponseEntity>> {
    return apiGet('/referrals/' + referralId + '/ime/scorecard-response', abortSignal);
}

export function getReferralSubmission(referralId: number, abortSignal: AbortSignal): Promise<any> {
    return apiGet('/referrals/' + referralId + '/referral-submission', abortSignal);
}

export function getReferralPhysicalTherapy(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<MedicalServiceDataModel> {
    return apiGet('/referrals/' + referralId + '/physical-therapy', abortSignal);
}

export function getReferralDiagnostic(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<DiagnosticServiceDataModel> {
    return apiGet('/referrals/' + referralId + '/diagnostic', abortSignal);
}

export function getReferralUr(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<UtilizationReviewServiceDataModel> {
    return apiGet('/referrals/' + referralId + '/utilization-review', abortSignal);
}

export function getReferralTransportation(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<TransportationDataModel> {
    return apiGet('/referrals/' + referralId + '/transportation', abortSignal);
}

export function getReferralTranslation(referralId: number, abortSignal: AbortSignal): Promise<TranslationDataModel> {
    return apiGet('/referrals/' + referralId + '/translation', abortSignal);
}

export function getReferralDme(referralId: number, abortSignal: AbortSignal): Promise<DmeServiceDataModel> {
    return apiGet('/referrals/' + referralId + '/dme', abortSignal);
}

export function getReferralDmeResponse(referralId: number, abortSignal: AbortSignal): Promise<DmeResponseDataModel> {
    return apiGet('/referrals/' + referralId + '/dme/response', abortSignal);
}

export function getReferralSiuService(referralId: number, abortSignal: AbortSignal): Promise<SurveillanceServiceDataModel> {
    return apiGet('/referrals/' + referralId + '/surveillance', abortSignal);
}

export type PutReferralSiuCaseDetailsBody = {
    /** Rush + adjuster comments (persisted in ReferralSiuCaseDetails). */
    caseDetails: {
        rushRequest: boolean;
        additionalInformation?: string | null;
    };
    eRemoteUnmannedNumberOfDays?: number | null;
    surveillanceMannedNumberOfDays?: number | null;
    medicalCanvassNumberOfFacilities?: number | null;
    /** When present (including empty array), replaces Medical Canvass junction rows. Omit when not updating. */
    medicalCanvassFacilityTypeIds?: number[];
    backgroundInvestigationOption?: string | null;
    eRemoteUnmannedOption?: string | null;
};

export function putReferralSiuCaseDetails(
    referralId: number,
    body: PutReferralSiuCaseDetailsBody,
    abortSignal: AbortSignal,
): Promise<SurveillanceServiceDataModel> {
    return apiPutPath<SurveillanceServiceDataModel>(
        '/referrals/' + referralId + '/surveillance/case-details',
        body,
        abortSignal,
    );
}

export function getReferralUrResponse(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<UtilizationReviewServiceDataModel> {
    return apiGet('/referrals/' + referralId + '/utilization-review', abortSignal);
}

export function getImeCoverLetter(referralId: number, abortSignal: AbortSignal): Promise<IMECoverLetterEntity> {
    return apiGet('/referrals/' + referralId + '/cover-letter-ime', abortSignal);
}

export function getCoverLetter(referralId: number, abortSignal: AbortSignal): Promise<IMECoverLetterEntity> {
    return apiGet('/referrals/' + referralId + '/cover-letter', abortSignal);
}

export function getTcmCoverLetter(referralId: number, abortSignal: AbortSignal): Promise<TcmCoverLetterEntity> {
    return apiGet('/referrals/' + referralId + '/cover-letter-tcm', abortSignal);
}

export function getReferralTcmTelephonicCaseManagement(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<TcmTelephonicCaseManagementEntity> {
    return apiGet('/referrals/' + referralId + '/tcm-telephonic-case-management', abortSignal);
}

export function getOverviewFcm(referralId: number, abortSignal: AbortSignal): Promise<ReferralOverviewFcmDataModel> {
    return apiGet('/referrals/' + referralId + '/overview-fcm', abortSignal);
}

export function getReferralFcmNotes(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<Array<ReferralNoteFcmEntity>> {
    return apiGet('/fcm-answer/notes/' + referralId, abortSignal);
}

export function getReferralFcmServiceTypeRequest(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<ServiceTypeRequestFcmEntity> {
    return apiGet('/referrals/' + referralId + '/fcm-service-type-request', abortSignal);
}

export function putReferralFcmServiceTypeRequest(
    referralId: number,
    body: { rushRequest: boolean; additionalInformation: string | null },
    abortSignal: AbortSignal,
): Promise<ServiceTypeRequestFcmEntity> {
    return apiPutPath<ServiceTypeRequestFcmEntity>(
        '/referrals/' + referralId + '/fcm/service-type-request',
        body,
        abortSignal,
    );
}

export function getReferralRtwServiceTypeRequest(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<ServiceTypeRequestRtwEntity> {
    return apiGet<ServiceTypeRequestRtwEntity>('/referrals/' + referralId + '/rtw-service-type-request', abortSignal);
}

export function putReferralRtwServiceTypeRequest(
    referralId: number,
    body: {
        rushRequest: boolean;
        physicalRestrictions: string;
        primaryLanguage: string;
        hourlyRate: string;
        isUnionMember: string;
        requiresAlternateHoursEnabled: boolean;
        transitionHoursPerWeek: string | null;
        requiresAlternateScheduleEnabled: boolean;
        workSchedulePreferences: string | null;
        lightDutyLocation: string;
        additionalInformation: string | null;
    },
    abortSignal: AbortSignal,
): Promise<ServiceTypeRequestRtwEntity> {
    return apiPutPath<ServiceTypeRequestRtwEntity>(
        '/referrals/' + referralId + '/rtw/service-type-request',
        body,
        abortSignal,
    );
}

export function getReferralTcmMedicalInfo(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<TcmMedicalInfoEntity> {
    return apiGet('/referrals/' + referralId + '/tcm-medical-info', abortSignal);
}

export function getReferralTcmTreatingProvider(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<TcmTreatingProviderEntity> {
    return apiGet('/referrals/' + referralId + '/tcm-treating-provider', abortSignal);
}

export function getReferralTcmOfficeVisit(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<TcmOfficeVisitEntity> {
    return apiGet('/referrals/' + referralId + '/tcm-office-visit', abortSignal);
}

export function getReferralTcmWorkStatus(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<TcmWorkStatusEntity> {
    return apiGet('/referrals/' + referralId + '/tcm-work-status', abortSignal);
}

export function getReferralTcmNotes(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<Array<ReferralNoteTcmEntity>> {
    return apiGet('/tcm-answer/notes/' + referralId, abortSignal);
}

export function saveTcmCoverLetter(
    referralId: number,
    coverLetter: TcmCoverLetterEntity,
    abortSignal: AbortSignal,
): Promise<TcmCoverLetterEntity> {
    // Both POST and PUT endpoints call the same backend method (InsertOrUpdateTcmCoverLetter)
    // which decides INSERT vs UPDATE based on tcmCoverLetterId
    return apiPost<TcmCoverLetterEntity>(
        '/referrals/' + referralId + '/cover-letter-tcm',
        coverLetter,
        abortSignal
    );
}

export function saveTcmTelephonicCaseManagement(
    referralId: number,
    entity: TcmTelephonicCaseManagementEntity,
    abortSignal: AbortSignal,
): Promise<TcmTelephonicCaseManagementEntity> {
    return apiPost<TcmTelephonicCaseManagementEntity>(
        '/referrals/' + referralId + '/tcm-telephonic-case-management',
        entity,
        abortSignal
    );
}

export function saveTcmMedicalInfo(
    referralId: number,
    entity: TcmMedicalInfoEntity,
    abortSignal: AbortSignal,
): Promise<TcmMedicalInfoEntity> {
    return apiPost<TcmMedicalInfoEntity>(
        '/referrals/' + referralId + '/tcm-medical-info',
        entity,
        abortSignal
    );
}

export function saveTcmTreatingProvider(
    referralId: number,
    entity: TcmTreatingProviderEntity,
    abortSignal: AbortSignal,
): Promise<TcmTreatingProviderEntity> {
    return apiPost<TcmTreatingProviderEntity>(
        '/referrals/' + referralId + '/tcm-treating-provider',
        entity,
        abortSignal
    );
}

export function saveTcmOfficeVisit(
    referralId: number,
    entity: TcmOfficeVisitEntity,
    abortSignal: AbortSignal,
): Promise<TcmOfficeVisitEntity> {
    return apiPost<TcmOfficeVisitEntity>(
        '/referrals/' + referralId + '/tcm-office-visit',
        entity,
        abortSignal
    );
}

export function saveTcmWorkStatus(
    referralId: number,
    entity: TcmWorkStatusEntity,
    abortSignal: AbortSignal,
): Promise<TcmWorkStatusEntity> {
    return apiPost<TcmWorkStatusEntity>(
        '/referrals/' + referralId + '/tcm-work-status',
        entity,
        abortSignal
    );
}

export function sendSubmissionRequest(
    referralId: number,
    submission: SubmissionEntity,
    abortSignal: AbortSignal,
): Promise<SubmissionEntity> {
    return apiPost<SubmissionEntity>('/referrals/' + referralId + '/submission', submission, abortSignal);
}

export function sendReferralPT(
    referralId: number,
    physicalTherapyDataModel: MedicalServiceDataModel,
    abortSignal: AbortSignal,
): Promise<MedicalServiceDataModel> {
    return apiPost<MedicalServiceDataModel>(
        '/referrals/' + referralId + '/physical-therapy',
        physicalTherapyDataModel,
        abortSignal,
    );
}

export function sendReferralDX(
    referralId: number,
    diagnosticDataModel: DiagnosticServiceDataModel,
    abortSignal: AbortSignal,
): Promise<DiagnosticServiceDataModel> {
    return apiPost<DiagnosticServiceDataModel>(
        '/referrals/' + referralId + '/diagnostic',
        diagnosticDataModel,
        abortSignal,
    );
}

export function sendReferralUR(
    referralId: number,
    utilizationReviewDataModel: UtilizationReviewServiceDataModel,
    abortSignal: AbortSignal,
): Promise<UtilizationReviewServiceDataModel> {
    return apiPost<UtilizationReviewServiceDataModel>(
        '/referrals/' + referralId + '/utilization-review',
        utilizationReviewDataModel,
        abortSignal,
    );
}

export function sendReferralTt(
    referralId: number,
    translationDataModel: TranslationDataModel,
    transportationDataModel: TransportationDataModel,
    abortSignal: AbortSignal,
): Promise<TtDataModel> {
    return apiPost<TtDataModel>(
        '/referrals/' + referralId + '/tt',
        {
            translation: translationDataModel,
            transportation: transportationDataModel,
        },
        abortSignal,
    );
}

export function sendReferralDme(
    referralId: number,
    dmeDataModel: DmeServiceDataModel,
    abortSignal: AbortSignal,
): Promise<DmeServiceDataModel> {
    return apiPost<DmeServiceDataModel>('/referrals/' + referralId + '/dme', dmeDataModel, abortSignal);
}

export function deleteReferral(referralId: number, abortSignal: AbortSignal): Promise<ReferralEntity> {
    return apiDelete<ReferralEntity>('/referrals', referralId, abortSignal);
}

export function getDiagnosticProcedureResponseScorecard(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<Array<DiagnosticProcedureResponseScorecardTabDataModel>> {
    return apiGet<Array<DiagnosticProcedureResponseScorecardTabDataModel>>(
        '/referrals/' + referralId + '/dxs/scorecard-response',
        abortSignal,
    );
}

export function getFailedReferrals(abortSignal: AbortSignal): Promise<Array<ReferralErrorListDataModel>> {
    return apiGet<Array<ReferralErrorListDataModel>>('/referrals/admin/failed', abortSignal);
}

export function getReferralsSentCount(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<Array<AdminReferralSentCountDataModel>> {
    return apiGet<Array<AdminReferralSentCountDataModel>>(
        `/referrals/admin/table/${referralId}/sent-count`,
        abortSignal,
    );
}
export function getReferralsByDateAndService(
    serviceId: number,
    dateRangeFrom: string,
    dateRangeTo: string,
    abortSignal: AbortSignal,
): Promise<Array<AdminReferralByDateAndServiceDataModel>> {
    return apiGet<Array<AdminReferralByDateAndServiceDataModel>>(
        `/referrals/admin/count-by-date/${serviceId}?dateRangeFrom=${dateRangeFrom}&dateRangeTo=${dateRangeTo}`,
        abortSignal,
    );
}

export function updateReferralStatusWithDetail(
    referralId: number,
    referralStatusId: number,
    abortSignal: AbortSignal,
    statusDetails?: string 
): Promise<ReferralStatus> {
    const payload = { referralStatusId, statusDetails };
    return apiPut<ReferralStatus>('/referrals/adjuster-status', referralId, payload, abortSignal);
}

export function updateReferralStatus(
    referralId: number,
    referralStatusId: number,
    abortSignal: AbortSignal,
): Promise<number> {
    return apiPut<number>('/referrals/status', referralId, referralStatusId, abortSignal);
}

const getQueryString = ({ tenantId, vendorId, dateRangeFrom, dateRangeTo }: ReferralSearchFilter) => {
    let queryString = '';

    if (tenantId) {
        queryString += 'tenantId=' + tenantId;
    }

    if (vendorId) {
        queryString += '&vendorId=' + vendorId;
    }

    if (dateRangeFrom) {
        queryString += '&dateRangeFrom=' + dateRangeFrom;
    }

    if (dateRangeTo) {
        queryString += '&dateRangeTo=' + dateRangeTo;
    }

    return queryString;
};

export function getReferralsTable(
    { tenantId, vendorId, dateRangeFrom, dateRangeTo }: ReferralSearchFilter,
    abortSignal: AbortSignal,
): Promise<Array<AdminReferralTableDataModel>> {
    const queryString = getQueryString({ tenantId, vendorId, dateRangeFrom, dateRangeTo });
    return apiGet<Array<AdminReferralTableDataModel>>(`/referrals/admin/table?${queryString}`, abortSignal);
}

export function getReferralsCountByDateTable(
    { tenantId, vendorId, dateRangeFrom, dateRangeTo }: ReferralSearchFilter,
    abortSignal: AbortSignal,
): Promise<Array<AdminReferralSentCountByDateDataModel>> {
    const queryString = getQueryString({ tenantId, vendorId, dateRangeFrom, dateRangeTo });
    return apiGet<Array<AdminReferralSentCountByDateDataModel>>(
        `/referrals/admin/count-by-date?${queryString}`,
        abortSignal,
    );
}

export function getReferralsVendorDocumentsTable(
    { tenantId, vendorId, dateRangeFrom, dateRangeTo }: ReferralSearchFilter,
    abortSignal: AbortSignal,
): Promise<Array<AdminReferralVendorDocumentsTableDataModel>> {
    const queryString = getQueryString({ tenantId, vendorId, dateRangeFrom, dateRangeTo });
    return apiGet<Array<AdminReferralVendorDocumentsTableDataModel>>(
        `/referrals/admin/vendor-documents-table?${queryString}`,
        abortSignal,
    );
}

export function getReferralsVendorDocumentsList(
    referralId: number,
    abortSignal: AbortSignal,
): Promise<Array<ReferralVendorDocumentDataModel>> {
    return apiGet<Array<ReferralVendorDocumentDataModel>>(
        '/referrals/admin/' + referralId + '/vendor-documents-list',
        abortSignal,
    );
}

export function GetSearchAddress(
    partialAddress: string,
    abortSignal: AbortSignal,
): Promise<string> {
    return apiGet<string>(
        '/search-address?query=' + partialAddress,
        abortSignal,
    );
}

export function getSiuNote(referralId: number, abortSignal: AbortSignal): Promise<Array<SiuVendorNote>> {
    return apiGet<Array<SiuVendorNote>>('/siu-answer/note/' + referralId, abortSignal);
}

export function getRtwNote(referralId: number, abortSignal: AbortSignal): Promise<Array<RtwVendorNote>> {
    return apiGet<Array<RtwVendorNote>>('/rtw-answer/note/' + referralId, abortSignal);
}

export function getSiuSummary(referralId: number, abortSignal: AbortSignal): Promise<SiuVendorSummary> {
    return apiGet<SiuVendorSummary>('/siu-answer/summary/' + referralId, abortSignal);
}